import React, { useMemo, useLayoutEffect, useState, useEffect, useCallback, useRef } from "react";
import { View, StyleSheet, TextInput, Pressable, Platform, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import { router } from "expo-router";
import { StackNavigationProp } from "@react-navigation/stack";
import { Table, ColumnDef, DurationCell } from "@/components/NewTable";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { userService } from "@/services/userService";
import { shiftsService } from "@/services/shiftService";
import { AssignableUser } from "@/types/db/assignable_user";
import Avatar from "@/components/Avatar";
import { usePositions } from "../PositionsContext";
import { useToolTip } from "@/hooks/useToolTip";
import { HeaderProgress } from "../components/HeaderProgress";
import { SmallToggle } from "@/components/SmallToggle";
import { ArrayChips } from "@/components/Table/ArrayChips";
import Badge from "@/components/ui/Badge";
import { supabase } from "@/lib/supabase-client";
import useContextMenu from "@/hooks/useContextMenu";
import useAuth from "@/hooks/useAuth";
import usePermissions from "@/hooks/usePermissions";
import useNavigationGuard from "@/hooks/useNavigationGuard";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";

type RootStackParamList = {
  Positions: undefined;
  AddUsers: { shiftPositionId?: string };
  AssignedUsers: { shiftPositionId?: string };
};

// Extend AssignableUser with display fields
type AvailableUser = AssignableUser;

interface HeaderButtonProps {
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  tooltip: string;
  onPress: () => void;
  disabled?: boolean;
}

function HeaderButton({ icon, tooltip, onPress, disabled }: HeaderButtonProps) {
  const { colors } = useTheme();
  const toolTip = useToolTip();
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const handlePointerEnter = useCallback(() => {
    if (disabled) return;
    isHovering.current = true;
    toolTip.setToolTipContent(
      <View
        style={{
          backgroundColor: colors.background,
          paddingHorizontal: 8,
          paddingVertical: 4,
          borderRadius: 8,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: colors.border,
        }}
      >
        <ThemedText style={{ fontSize: 12 }} numberOfLines={1}>
          {tooltip}
        </ThemedText>
      </View>,
    );
    ref.current?.measureInWindow((x, y) => {
      if (!isHovering.current) return;
      toolTip.setLocation({ x: x - 60, y: y - 36 } as any);
      toolTip.setVisible(true);
    });
  }, [tooltip, colors.background, colors.border, toolTip, disabled]);

  const handlePointerLeave = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  const handlePress = useCallback(() => {
    if (disabled) return;
    isHovering.current = false;
    toolTip.setVisible(false);
    onPress();
  }, [onPress, toolTip, disabled]);

  return (
    <Pressable
      ref={ref}
      style={{ marginRight: 16, padding: 4, opacity: disabled ? 0.4 : 1 }}
      onPress={handlePress}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      disabled={disabled}
    >
      <MaterialCommunityIcons name={icon} size={24} color={disabled ? colors.textSecondary : colors.primary} />
    </Pressable>
  );
}

// Component for displaying scheduling conflicts with tooltip
interface ConflictsCellProps {
  conflicts: {
    shift_id: string;
    shift_name: string | null;
    position_name: string | null;
    start_time: string;
    end_time: string;
  }[];
}

function ConflictsCell({ conflicts }: ConflictsCellProps) {
  const { colors } = useTheme();
  const toolTip = useToolTip();
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const handlePointerEnter = useCallback(() => {
    isHovering.current = true;
    toolTip.setToolTipContent(
      <View
        style={{
          backgroundColor: colors.background,
          paddingHorizontal: 12,
          paddingVertical: 8,
          borderRadius: 8,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: colors.border,
        }}
      >
        <ThemedText style={{ fontSize: 12, fontWeight: "600", marginBottom: 6 }}>Scheduling Conflicts</ThemedText>
        {conflicts.map((shift, idx) => (
          <View key={shift.shift_id || idx} style={{ marginBottom: idx < conflicts.length - 1 ? 4 : 0 }}>
            <ThemedText style={{ fontSize: 12 }}>
              {shift.shift_name || "Unnamed Shift"}
              {shift.position_name && ` - ${shift.position_name}`}
            </ThemedText>
            <ThemedText style={{ fontSize: 11, color: colors.textSecondary }}>
              {new Date(shift.start_time).toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}{" "}
              -{" "}
              {new Date(shift.end_time).toLocaleString(undefined, {
                hour: "numeric",
                minute: "2-digit",
              })}
            </ThemedText>
          </View>
        ))}
      </View>,
    );
    ref.current?.measureInWindow((x, y, width) => {
      if (!isHovering.current) return;
      // Position tooltip centered above the element
      toolTip.setLocation({ x: x + width / 2, y: y - 8 } as any);
      toolTip.setVisible(true);
    });
  }, [conflicts, colors, toolTip]);

  const handlePointerLeave = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  return (
    <Pressable
      ref={ref}
      style={{ flexDirection: "row", alignItems: "center", gap: 4 }}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      <MaterialCommunityIcons name="calendar-alert" size={16} color={colors.amber?.text || "#f59e0b"} />
      <ThemedText style={{ fontSize: 12, color: colors.amber?.text || "#f59e0b" }}>{conflicts.length}</ThemedText>
    </Pressable>
  );
}

export default function AddUsersScreen() {
  const { colors } = useTheme();
  const { displayNames } = useAuth();
  const { roles: rolesPermissions } = usePermissions();
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "AddUsers">>();
  const {
    positions,
    shiftId,
    addUserToPosition,
    shiftMarkets,
    projectMarkets,
    shiftLocation,
    projectLocation,
    clientAbilities,
    projectAbilities,
    shiftAbilities,
    clientRestrictions,
    projectRestrictions,
    shiftRestrictions,
  } = usePositions();
  const contextMenu = useContextMenu();
  const currentEntity = useCurrentEntity();
  const { pushNavigation } = useNavigationHistory();
  const { navigateToEntity } = useNavigationGuard();
  const filterButtonRef = useRef<View>(null);
  const isFilterMenuOpen = useRef(false);

  // Navigation handler for user names
  const handleUserPress = useCallback(
    (user: AvailableUser) => {
      // Save current entity to navigation history before navigating
      if (currentEntity?.currentPanel && currentEntity?.currentId) {
        pushNavigation({
          panel: currentEntity.currentPanel,
          id: currentEntity.currentId,
          title: currentEntity.currentTitle || currentEntity.currentPanel,
        });
      }
      // Navigate to user profile
      navigateToEntity("user", user.user_id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEntity, pushNavigation],
  );

  // Display name helpers
  const projectName = displayNames?.projects?.name || "Project";
  const shiftName = displayNames?.shifts?.name || "Shift";

  const [users, setUsers] = useState<AvailableUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

  // Filter toggles - qualified always shown, others hidden by default
  const [showUnqualified, setShowUnqualified] = useState(false);
  const [showUnavailable, setShowUnavailable] = useState(false);
  const [showWithoutPosition, setShowWithoutPosition] = useState(false);
  const [showBanned, setShowBanned] = useState(false);
  const [showRestricted, setShowRestricted] = useState(false);

  // Markets filter
  const [allMarkets, setAllMarkets] = useState<{ id: string; name: string }[]>([]);
  const [selectedMarketIds, setSelectedMarketIds] = useState<Set<string> | null>(null); // null = not initialized
  const [showNoMarket, setShowNoMarket] = useState(false);

  // Fetch all markets from database
  useEffect(() => {
    (async () => {
      const { data: marketsData } = await supabase.schema("lists").from("markets").select("id, name").order("name");
      if (marketsData) {
        setAllMarkets(marketsData);
      }
    })();
  }, []);

  // Build market ID to name map
  const marketMap = useMemo(() => {
    return Object.fromEntries(allMarkets.map((m) => [m.id, m.name]));
  }, [allMarkets]);

  // Compute deduplicated market list with sources
  // Sources: Project Location → Project Meta → Shift Location → Shift Meta
  const availableMarkets = useMemo(() => {
    type SourceType = "projectLocation" | "project" | "shiftLocation" | "shift";
    const marketSources = new Map<string, { id: string; sources: Set<SourceType> }>();

    const addMarket = (marketId: string | undefined, source: SourceType) => {
      // Only add if market exists in the marketMap (valid market)
      if (!marketId || !marketMap[marketId]) return;
      if (!marketSources.has(marketId)) {
        marketSources.set(marketId, { id: marketId, sources: new Set() });
      }
      marketSources.get(marketId)!.sources.add(source);
    };

    // Project location market (lowest priority, first in chain)
    addMarket(projectLocation?.market_id, "projectLocation");

    // Project meta_data markets
    for (const marketId of projectMarkets || []) {
      addMarket(marketId, "project");
    }

    // Shift location market (overrides project location)
    addMarket(shiftLocation?.market_id, "shiftLocation");

    // Shift meta_data markets
    for (const marketId of shiftMarkets || []) {
      addMarket(marketId, "shift");
    }

    return Array.from(marketSources.values()).map((m) => {
      const sources = Array.from(m.sources);
      // Build a display string for source
      let sourceDisplay: string;
      if (sources.length === 1) {
        const source = sources[0];
        if (source === "projectLocation") sourceDisplay = `${projectName} Location`;
        else if (source === "project") sourceDisplay = projectName;
        else if (source === "shiftLocation") sourceDisplay = `${shiftName} Location`;
        else sourceDisplay = shiftName;
      } else if (sources.length === 2 && sources.includes("projectLocation") && sources.includes("shiftLocation")) {
        sourceDisplay = "Both Locations";
      } else if (sources.length === 2 && sources.includes("project") && sources.includes("shift")) {
        sourceDisplay = "Both";
      } else {
        sourceDisplay = "Multiple";
      }
      return {
        id: m.id,
        name: marketMap[m.id],
        source: sourceDisplay,
        sources,
      };
    });
  }, [
    projectLocation?.market_id,
    projectMarkets,
    shiftLocation?.market_id,
    shiftMarkets,
    marketMap,
    projectName,
    shiftName,
  ]);

  // Compute other markets (all markets minus effective markets)
  const otherMarkets = useMemo(() => {
    const effectiveIds = new Set(availableMarkets.map((m) => m.id));
    return allMarkets.filter((m) => !effectiveIds.has(m.id));
  }, [allMarkets, availableMarkets]);

  // Initialize selected markets with all effective markets on first load
  useEffect(() => {
    if (selectedMarketIds === null && availableMarkets.length > 0) {
      setSelectedMarketIds(new Set(availableMarkets.map((m) => m.id)));
    }
  }, [availableMarkets, selectedMarketIds]);

  // Use empty set if not initialized yet
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const effectiveSelectedMarketIds = selectedMarketIds ?? new Set<string>();

  // Check if filter is modified from default (some effective markets unchecked, other markets added, or showNoMarket)
  const allEffectiveSelected = availableMarkets.every((m) => effectiveSelectedMarketIds.has(m.id));
  const hasOtherMarketsSelected = otherMarkets.some((m) => effectiveSelectedMarketIds.has(m.id));
  const hasActiveFilter = !allEffectiveSelected || hasOtherMarketsSelected || showNoMarket;

  // Compute effective abilities (deduplicated union of client -> project -> shift)
  const effectiveAbilities = useMemo(() => {
    const map = new Map<string, { id: string; name: string; source: string }>();
    clientAbilities?.forEach((a) => map.set(a.id, { ...a, source: "Client" }));
    projectAbilities?.forEach((a) => {
      const existing = map.get(a.id);
      map.set(a.id, { ...a, source: existing ? "Multiple" : "Project" });
    });
    shiftAbilities?.forEach((a) => {
      const existing = map.get(a.id);
      map.set(a.id, { ...a, source: existing ? "Multiple" : "Shift" });
    });
    return Array.from(map.values());
  }, [clientAbilities, projectAbilities, shiftAbilities]);

  // Compute effective restrictions (deduplicated union of client -> project -> shift)
  const effectiveRestrictions = useMemo(() => {
    const map = new Map<string, { id: string; name: string; source: string }>();
    clientRestrictions?.forEach((r) => map.set(r.id, { ...r, source: "Client" }));
    projectRestrictions?.forEach((r) => {
      const existing = map.get(r.id);
      map.set(r.id, { ...r, source: existing ? "Multiple" : "Project" });
    });
    shiftRestrictions?.forEach((r) => {
      const existing = map.get(r.id);
      map.set(r.id, { ...r, source: existing ? "Multiple" : "Shift" });
    });
    return Array.from(map.values());
  }, [clientRestrictions, projectRestrictions, shiftRestrictions]);

  // Build filter menu content
  const filterMenuContent = useMemo(
    () => (
      <View style={styles.filterPopover}>
        <View style={[styles.filterHeader, { borderBottomColor: colors.border }]}>
          <ThemedText type="defaultSemiBold" style={{ fontSize: 14 }}>
            Filter by Market
          </ThemedText>
          {hasActiveFilter && (
            <Pressable
              onPress={() => {
                setSelectedMarketIds(new Set(availableMarkets.map((m) => m.id)));
                setShowNoMarket(false);
              }}
            >
              <ThemedText style={{ fontSize: 12, color: colors.primary }}>Reset</ThemedText>
            </Pressable>
          )}
        </View>

        <ScrollView style={{ maxHeight: 400 }} showsVerticalScrollIndicator>
          {/* Effective Markets Section */}
          {availableMarkets.length > 0 && (
            <>
              <View style={[styles.sectionDivider, { borderBottomColor: colors.border }]}>
                <ThemedText
                  style={{
                    fontSize: 11,
                    color: colors.textSecondary,
                    fontWeight: "600",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Effective Markets
                </ThemedText>
              </View>
              {availableMarkets.map((market) => (
                <Pressable
                  key={market.id}
                  style={styles.filterOption}
                  onPress={() => {
                    setSelectedMarketIds((prev) => {
                      const current = prev ?? new Set<string>();
                      const next = new Set(current);
                      if (next.has(market.id)) next.delete(market.id);
                      else next.add(market.id);
                      return next;
                    });
                  }}
                >
                  <MaterialCommunityIcons
                    name={effectiveSelectedMarketIds.has(market.id) ? "checkbox-marked" : "checkbox-blank-outline"}
                    size={20}
                    color={effectiveSelectedMarketIds.has(market.id) ? colors.primary : colors.textSecondary}
                  />
                  <ThemedText style={{ flex: 1, fontSize: 13 }} numberOfLines={1}>
                    {market.name}
                  </ThemedText>
                  <View
                    style={[
                      styles.sourceChip,
                      {
                        backgroundColor:
                          market.sources.includes("projectLocation") || market.sources.includes("shiftLocation")
                            ? "#9C27B020"
                            : market.source === "Both" || market.source === "Multiple"
                              ? colors.primary + "20"
                              : market.sources.includes("project")
                                ? "#4CAF5020"
                                : "#FF980020",
                      },
                    ]}
                  >
                    <ThemedText
                      style={{
                        fontSize: 10,
                        color:
                          market.sources.includes("projectLocation") || market.sources.includes("shiftLocation")
                            ? "#9C27B0"
                            : market.source === "Both" || market.source === "Multiple"
                              ? colors.primary
                              : market.sources.includes("project")
                                ? "#4CAF50"
                                : "#FF9800",
                      }}
                    >
                      {market.source}
                    </ThemedText>
                  </View>
                </Pressable>
              ))}
            </>
          )}

          {/* All Markets Section */}
          {otherMarkets.length > 0 && (
            <>
              <View style={[styles.sectionDivider, { borderBottomColor: colors.border }]}>
                <ThemedText
                  style={{
                    fontSize: 11,
                    color: colors.textSecondary,
                    fontWeight: "600",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  All Markets
                </ThemedText>
              </View>
              {otherMarkets.map((market) => (
                <Pressable
                  key={market.id}
                  style={styles.filterOption}
                  onPress={() => {
                    setSelectedMarketIds((prev) => {
                      const current = prev ?? new Set<string>();
                      const next = new Set(current);
                      if (next.has(market.id)) next.delete(market.id);
                      else next.add(market.id);
                      return next;
                    });
                  }}
                >
                  <MaterialCommunityIcons
                    name={effectiveSelectedMarketIds.has(market.id) ? "checkbox-marked" : "checkbox-blank-outline"}
                    size={20}
                    color={effectiveSelectedMarketIds.has(market.id) ? colors.primary : colors.textSecondary}
                  />
                  <ThemedText style={{ flex: 1, fontSize: 13 }} numberOfLines={1}>
                    {market.name}
                  </ThemedText>
                </Pressable>
              ))}
            </>
          )}

          {/* Other Section */}
          <View style={[styles.sectionDivider, { borderBottomColor: colors.border }]}>
            <ThemedText
              style={{
                fontSize: 11,
                color: colors.textSecondary,
                fontWeight: "600",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Other
            </ThemedText>
          </View>
          <Pressable style={styles.filterOption} onPress={() => setShowNoMarket((v) => !v)}>
            <MaterialCommunityIcons
              name={showNoMarket ? "checkbox-marked" : "checkbox-blank-outline"}
              size={20}
              color={showNoMarket ? colors.primary : colors.textSecondary}
            />
            <ThemedText style={{ flex: 1, fontSize: 13 }}>No Market</ThemedText>
            <View style={[styles.sourceChip, { backgroundColor: colors.backgroundSecondary }]}>
              <ThemedText style={{ fontSize: 10, color: colors.textSecondary }}>Users without market</ThemedText>
            </View>
          </Pressable>

          {availableMarkets.length === 0 && otherMarkets.length === 0 && (
            <View style={styles.filterEmpty}>
              <ThemedText style={{ fontSize: 12, color: colors.textSecondary }}>No markets configured</ThemedText>
            </View>
          )}
        </ScrollView>
      </View>
    ),
    [colors, hasActiveFilter, showNoMarket, availableMarkets, effectiveSelectedMarketIds, otherMarkets],
  );

  // Close context menu when this screen unmounts to prevent it persisting across navigation
  useEffect(() => {
    return () => {
      if (isFilterMenuOpen.current) {
        contextMenu.setVisible(false);
        isFilterMenuOpen.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update context menu content when filter state changes (only if this component opened it)
  useEffect(() => {
    if (contextMenu.visible && isFilterMenuOpen.current) {
      contextMenu.setMenuContent(filterMenuContent);
    }
    // Reset flag when menu closes
    if (!contextMenu.visible) {
      isFilterMenuOpen.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMenuContent, contextMenu.visible]);

  const shiftPositionId = route.params?.shiftPositionId;
  const position = positions.find((p) => p.shift_position_id === shiftPositionId);

  // Get already invited user IDs for this position
  const invitedUserIds = useMemo(() => {
    if (!position?.invited_users) return new Set<string>();
    return new Set(position.invited_users.map((u) => u.user?.id || u.id));
  }, [position]);

  // Get effective ability/restriction IDs to pass to the RPC
  const effectiveAbilityIds = useMemo(() => effectiveAbilities.map((a) => a.id), [effectiveAbilities]);
  const effectiveRestrictionIds = useMemo(() => effectiveRestrictions.map((r) => r.id), [effectiveRestrictions]);

  // Fetch available users using RPC for rich data
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);

      try {
        if (!shiftId) {
          setUsers([]);
          return;
        }

        const data = await userService.getAssignableUsers({
          shiftId,
          positionId: position?.position_id || undefined,
          requiredAbilityIds: effectiveAbilityIds,
          requiredRestrictionIds: effectiveRestrictionIds,
        });

        // Filter out already invited users
        const availableUsers = data.filter((u) => !invitedUserIds.has(u.user_id));
        setUsers(availableUsers);
      } catch (err: any) {
        console.error("Failed to fetch users:", err);
        setError(err.message || "Failed to load users");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [shiftId, position?.position_id, invitedUserIds, effectiveAbilityIds, effectiveRestrictionIds]);

  const handleAddSelectedUsers = useCallback(async () => {
    if (!shiftPositionId || selectedUserIds.size === 0) return;

    const userIdsArray = Array.from(selectedUserIds);

    try {
      // Single batch insert
      await shiftsService.addShiftUsersBatch(shiftPositionId, userIdsArray, "QUEUED");

      // Update context for all users at once
      for (const userId of userIdsArray) {
        const user = users.find((u) => u.user_id === userId);
        if (user) {
          addUserToPosition(shiftPositionId, {
            id: user.user_id,
            status: "QUEUED",
            meta_data: {},
            user: {
              id: user.user_id,
              first_name: user.first_name,
              last_name: user.last_name,
              preferred_name: user.preferred_name,
              email_address: user.email_address,
            },
          });
        }
      }

      // Remove all added users from available list at once
      setUsers((prev) => prev.filter((u) => !selectedUserIds.has(u.user_id)));
      setSelectedUserIds(new Set());
    } catch (err) {
      console.error("Failed to add users:", err);
    }
  }, [shiftPositionId, selectedUserIds, users, addUserToPosition]);

  // Set up header
  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => (
        <View style={styles.headerTitleContainer}>
          <View style={styles.headerTitleText}>
            <ThemedText style={styles.title}>{position?.name || "Add Users"}</ThemedText>
            <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>
              {users.length} available user{users.length !== 1 ? "s" : ""}
            </ThemedText>
          </View>
          <View style={[styles.headerSearchContainer, { backgroundColor: colors.backgroundSecondary }]}>
            <MaterialCommunityIcons
              name="magnify"
              size={16}
              color={colors.textSecondary}
              style={styles.headerSearchIcon}
            />
            <TextInput
              value={searchValue}
              onChangeText={setSearchValue}
              placeholder="Search..."
              placeholderTextColor={colors.textSecondary}
              style={[styles.headerSearchInput, { color: colors.text }]}
              autoCapitalize="none"
              autoCorrect={false}
            />
            {searchValue.length > 0 && (
              <Pressable onPress={() => setSearchValue("")} style={styles.headerClearButton}>
                <MaterialCommunityIcons name="close-circle" size={14} color={colors.textSecondary} />
              </Pressable>
            )}
          </View>
          <HeaderProgress
            current={position?.invited_users?.length ?? 0}
            pending={selectedUserIds.size}
            needed={position?.quantity ?? 1}
            preferred={position?.preferred_quantity ?? position?.quantity ?? 1}
          />
        </View>
      ),
      headerRight: () => (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 16, marginRight: 8 }}>
          <Pressable
            ref={filterButtonRef}
            style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
            onPress={() => {
              filterButtonRef.current?.measureInWindow((x, y, width, height) => {
                isFilterMenuOpen.current = true;
                contextMenu.setTriggerElement?.({ x, y, width, height });
                contextMenu.setLocation({ x, y: y + height + 4 });
                contextMenu.setMenuContent(filterMenuContent);
                contextMenu.setVisible(true);
              });
            }}
          >
            <MaterialCommunityIcons
              name="filter-variant"
              size={20}
              color={hasActiveFilter ? colors.primary : colors.text}
            />
            <ThemedText
              style={{
                fontSize: 14,
                opacity: hasActiveFilter ? 1 : 0.8,
                color: hasActiveFilter ? colors.primary : colors.text,
              }}
            >
              Markets ({effectiveSelectedMarketIds.size}
              {showNoMarket ? "+1" : ""})
            </ThemedText>
          </Pressable>
          <SmallToggle value={showUnqualified} onValueChange={setShowUnqualified} label="Unqualified" />
          <SmallToggle value={showUnavailable} onValueChange={setShowUnavailable} label="Unavailable" />
          <SmallToggle value={showWithoutPosition} onValueChange={setShowWithoutPosition} label="No Position" />
          <SmallToggle value={showRestricted} onValueChange={setShowRestricted} label="Restricted" />
          <SmallToggle value={showBanned} onValueChange={setShowBanned} label="Banned" />
          <HeaderButton
            icon="plus"
            tooltip={
              selectedUserIds.size > 0
                ? `Add ${selectedUserIds.size} User${selectedUserIds.size > 1 ? "s" : ""}`
                : "Select users to add"
            }
            onPress={handleAddSelectedUsers}
            disabled={selectedUserIds.size === 0}
          />
        </View>
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    navigation,
    position?.name,
    users.length,
    colors,
    searchValue,
    selectedUserIds.size,
    handleAddSelectedUsers,
    showUnqualified,
    showUnavailable,
    showWithoutPosition,
    showRestricted,
    showBanned,
    hasActiveFilter,
    effectiveSelectedMarketIds.size,
    showNoMarket,
    filterMenuContent,
    contextMenu,
  ]);

  const formatUserName = (user: AvailableUser) => {
    const firstName = user.preferred_name || user.first_name || "";
    const lastName = user.last_name || "";
    return `${firstName} ${lastName}`.trim() || "—";
  };

  const handleAddUser = async (user: AvailableUser) => {
    if (!shiftPositionId) {
      console.error("Missing shiftPositionId");
      return;
    }

    try {
      await shiftsService.addShiftUser(shiftPositionId, user.user_id, "QUEUED");

      // Update context so AssignedUsersScreen sees the new user
      addUserToPosition(shiftPositionId, {
        id: user.user_id,
        status: "QUEUED",
        meta_data: {},
        user: {
          id: user.user_id,
          first_name: user.first_name,
          last_name: user.last_name,
          preferred_name: user.preferred_name,
          email_address: user.email_address,
        },
      });

      // Remove user from available list
      setUsers((prev) => prev.filter((u) => u.user_id !== user.user_id));
      // Remove from selection if selected
      setSelectedUserIds((prev) => {
        const next = new Set(prev);
        next.delete(user.user_id);
        return next;
      });
    } catch (err: any) {
      console.error("Failed to add user:", err);
      setError(err.message || "Failed to add user");
    }
  };

  const columns: ColumnDef<AvailableUser>[] = useMemo(
    () => [
      {
        key: "avatar",
        header: "",
        width: 48,
        align: "center",
        sortable: false,
        searchable: false,
        render: (_, row) => (
          <Avatar
            userId={row.user_id}
            email={row.email_address ?? undefined}
            firstName={row.preferred_name || row.first_name || ""}
            lastName={row.last_name || ""}
            size={30}
          />
        ),
      },
      {
        key: "name",
        header: "Name",
        flex: 1,
        minWidth: 150,
        accessor: (row) => formatUserName(row),
        onPress: handleUserPress,
      },
      {
        key: "conflicts",
        header: "Conflicts",
        width: 90,
        minWidth: 100,
        align: "center",
        sortable: true,
        searchable: false,
        accessor: (row) => row.scheduled_shifts?.length ?? 0,
        render: (_, row) => {
          const conflicts = row.scheduled_shifts || [];
          if (conflicts.length === 0) {
            return <ThemedText style={{ fontSize: 12, opacity: 0.4 }}>—</ThemedText>;
          }
          return <ConflictsCell conflicts={conflicts} />;
        },
      },
      {
        key: "banned",
        header: "Banned",
        width: 180,
        sortable: false,
        searchable: false,
        render: (_, row) => {
          // Only show for banned users with specific source
          if (row.is_banned_from_client && row.is_banned_from_location) {
            return <Badge text="Client & Location" tone="red" />;
          }
          if (row.is_banned_from_client) {
            return <Badge text="Client" tone="red" />;
          }
          if (row.is_banned_from_location) {
            return <Badge text="Location" tone="red" />;
          }
          return <ThemedText style={{ fontSize: 12, opacity: 0.4 }}>—</ThemedText>;
        },
      },
      {
        key: "markets",
        header: "Markets",
        width: 180,
        flex: 3,
        searchable: true,
        searchValue: (_, row) => {
          const markets = (row.meta_data as any)?.markets || [];
          return markets.map((m: string) => marketMap[m] || m).join(" ");
        },
        render: (_, row) => {
          const markets = (row.meta_data as any)?.markets || [];
          if (markets.length === 0) return <ThemedText style={{ fontSize: 12 }}>—</ThemedText>;
          return <ArrayChips values={markets} displayMap={marketMap} chipColor="#4169E1" />;
        },
      },
      {
        key: "since_last",
        header: "Since Last",
        width: 130,
        align: "right",
        searchable: false,
        accessor: (row) => row.hours_since_last_shift ?? null,
        render: (value) => <DurationCell hours={value as number | null} />,
      },
      {
        key: "until_next",
        header: "Until Next",
        width: 130,
        align: "right",
        searchable: false,
        accessor: (row) => row.hours_until_next_shift ?? null,
        render: (value) => <DurationCell hours={value as number | null} />,
      },
      {
        key: "worked",
        header: "Worked",
        width: 100,
        align: "right",
        searchable: false,
        accessor: (row) => row.hours_worked_week ?? null,
        render: (value) => (
          <ThemedText style={{ fontSize: 12 }}>{value != null ? `${(value as number).toFixed(1)}h` : "—"}</ThemedText>
        ),
      },
      {
        key: "scheduled",
        header: "Scheduled",
        minWidth: 100,
        align: "right",
        searchable: false,
        accessor: (row) => row.hours_scheduled_week ?? null,
        render: (value) => (
          <ThemedText style={{ fontSize: 12 }}>{value != null ? `${(value as number).toFixed(1)}h` : "—"}</ThemedText>
        ),
      },
      {
        key: "overtime",
        header: "OT",
        width: 80,
        align: "right",
        searchable: false,
        accessor: (row) => {
          const worked = row.hours_worked_week ?? 0;
          const scheduled = row.hours_scheduled_week ?? 0;
          return Math.max(0, worked + scheduled - 40);
        },
        render: (value) => {
          const ot = value as number;
          return (
            <ThemedText style={{ fontSize: 12, color: ot > 0 ? "#f59e0b" : undefined }}>
              {ot > 0 ? `${ot.toFixed(1)}h` : "—"}
            </ThemedText>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [colors, marketMap, handleUserPress],
  );

  // Categorize a user based on their qualification status
  // Uses RPC flags: has_required_abilities (user has ALL required abilities from client/project/shift)
  // and has_blocking_restrictions (user has ANY restriction that matches client/project/shift)
  const categorizeUser = useCallback((user: AvailableUser) => {
    if (user.is_banned_from_client || user.is_banned_from_location) return "banned";
    if (user.has_blocking_restrictions) return "restricted";
    if (!user.has_required_abilities) return "unqualified";
    return "qualified";
  }, []);

  // Filter users by category toggles, markets, and search
  const filteredUsers = useMemo(() => {
    let result = users;

    // Apply category filters (qualified always shown)
    result = result.filter((user) => {
      const category = categorizeUser(user);
      switch (category) {
        case "qualified":
          return true; // Always show qualified users
        case "unqualified":
          return showUnqualified;
        case "banned":
          return showBanned;
        case "restricted":
          return showRestricted;
        default:
          return true; // Default to showing
      }
    });

    // Apply unavailable filter (users with scheduling conflicts)
    if (!showUnavailable) {
      result = result.filter((user) => {
        const conflicts = user.scheduled_shifts || [];
        return conflicts.length === 0;
      });
    }

    // Apply position filter (users who don't hold the shift's position)
    if (!showWithoutPosition) {
      result = result.filter((user) => user.holds_position === true);
    }

    // Apply market filter (ANY match - user has any of the selected markets)
    // Filter whenever there are selected markets or showNoMarket is enabled
    if (effectiveSelectedMarketIds.size > 0 || showNoMarket) {
      result = result.filter((user) => {
        const userMarkets: string[] = (user.meta_data as any)?.markets || [];

        // Check "No Market" filter
        if (showNoMarket && userMarkets.length === 0) return true;

        // Check if user has ANY of the selected markets
        if (effectiveSelectedMarketIds.size > 0) {
          return userMarkets.some((m) => effectiveSelectedMarketIds.has(m));
        }

        return false;
      });
    }

    // Apply search filter
    if (searchValue.trim()) {
      const search = searchValue.toLowerCase();
      result = result.filter((user) => {
        const name = formatUserName(user).toLowerCase();
        const email = (user.email_address || "").toLowerCase();
        return name.includes(search) || email.includes(search);
      });
    }

    return result;
  }, [
    users,
    searchValue,
    showUnqualified,
    showUnavailable,
    showWithoutPosition,
    showBanned,
    showRestricted,
    categorizeUser,
    effectiveSelectedMarketIds,
    showNoMarket,
  ]);

  // Group users by role
  const groupedUsers = useMemo(() => {
    const groups = new Map<string, AvailableUser[]>();
    for (const user of filteredUsers) {
      const roleName = user.role_name || "Unassigned";
      if (!groups.has(roleName)) groups.set(roleName, []);
      groups.get(roleName)!.push(user);
    }
    // Sort groups by priority (lowest = highest priority, appears first)
    return Array.from(groups.entries()).sort((a, b) => {
      const priorityA = a[1][0]?.role_priority ?? 10;
      const priorityB = b[1][0]?.role_priority ?? 10;
      return priorityA - priorityB || a[0].localeCompare(b[0]);
    });
  }, [filteredUsers]);

  // Check if any users have roles with priority configured
  const hasRolesWithPriority = useMemo(() => {
    return users.some((user) => user.role_priority != null);
  }, [users]);

  const toggleSection = (roleName: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(roleName)) {
        next.delete(roleName);
      } else {
        next.add(roleName);
      }
      return next;
    });
  };

  if (error) {
    return (
      <View style={[styles.container, { backgroundColor: colors.backgroundSecondary }]}>
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="alert-circle" size={48} color={colors.red.text} />
          <ThemedText style={styles.emptyText}>Failed to load users</ThemedText>
          <ThemedText style={{ opacity: 0.6, fontSize: 12 }}>{error}</ThemedText>
        </View>
      </View>
    );
  }

  // Guard: Check if roles have priority configured (only when users loaded but none have priority)
  if (!loading && users.length > 0 && !hasRolesWithPriority) {
    const canUpdateRoles = rolesPermissions.update;

    return (
      <View style={[styles.container, { backgroundColor: colors.backgroundSecondary }]}>
        <View style={styles.configurationRequired}>
          <MaterialCommunityIcons
            name="sort-numeric-ascending"
            size={64}
            color={colors.text}
            style={{ opacity: 0.3 }}
          />
          <ThemedText type="subtitle" style={styles.configTitle}>
            No Role Priorities Configured
          </ThemedText>
          <ThemedText style={styles.configMessage}>
            To assign users, roles must have priorities configured. Users are grouped and sorted by role priority when
            assigning.
          </ThemedText>
          {canUpdateRoles ? (
            <>
              <Pressable
                style={[styles.configLink, { backgroundColor: colors.primary }]}
                onPress={() => router.push("/settings/roles")}
              >
                <MaterialCommunityIcons name="cog" size={18} color="white" />
                <ThemedText style={styles.configLinkText}>Go to Role Settings</ThemedText>
              </Pressable>
              <View style={styles.configInstructions}>
                <ThemedText type="defaultSemiBold" style={styles.configStepsTitle}>
                  To configure role priorities:
                </ThemedText>
                <View style={styles.configStep}>
                  <ThemedText style={styles.configStepNumber}>1.</ThemedText>
                  <ThemedText style={styles.configStepText}>Select a role from the list</ThemedText>
                </View>
                <View style={styles.configStep}>
                  <ThemedText style={styles.configStepNumber}>2.</ThemedText>
                  <ThemedText style={styles.configStepText}>
                    Assign a priority from 1-10 (1 = highest priority, shown first)
                  </ThemedText>
                </View>
              </View>
            </>
          ) : (
            <View style={styles.configInstructions}>
              <ThemedText type="defaultSemiBold" style={styles.configStepsTitle}>
                Contact your system administrator
              </ThemedText>
              <ThemedText style={[styles.configMessage, { marginTop: 8 }]}>
                You don&apos;t have permission to update roles. Ask your administrator to configure role priorities:
              </ThemedText>
              <View style={[styles.configStep, { marginTop: 12 }]}>
                <ThemedText style={styles.configStepNumber}>1.</ThemedText>
                <ThemedText style={styles.configStepText}>Go to Settings → Roles</ThemedText>
              </View>
              <View style={styles.configStep}>
                <ThemedText style={styles.configStepNumber}>2.</ThemedText>
                <ThemedText style={styles.configStepText}>Select the roles to prioritize</ThemedText>
              </View>
              <View style={styles.configStep}>
                <ThemedText style={styles.configStepNumber}>3.</ThemedText>
                <ThemedText style={styles.configStepText}>
                  Assign a priority from 1-10 (1 = highest priority, shown first)
                </ThemedText>
              </View>
            </View>
          )}
        </View>
      </View>
    );
  }

  // Show loading or empty state within main layout
  if (loading || groupedUsers.length === 0) {
    return (
      <View style={[styles.container, { backgroundColor: colors.backgroundSecondary }]}>
        <Table<AvailableUser>
          data={[]}
          columns={columns}
          keyExtractor={(item) => item.user_id}
          loading={loading}
          loadingRowCount={8}
          emptyMessage="No available users"
          emptyIcon="account-outline"
        />
      </View>
    );
  }

  return (
    <>
      <ScrollView style={[styles.container, { backgroundColor: colors.backgroundSecondary }]}>
        {groupedUsers.map(([roleName, roleUsers]) => {
          const isCollapsed = collapsedSections.has(roleName);
          return (
            <View key={roleName} style={styles.roleSection}>
              <Pressable
                onPress={() => toggleSection(roleName)}
                style={[styles.sectionHeader, { borderBottomColor: colors.border }]}
              >
                <MaterialCommunityIcons
                  name={isCollapsed ? "chevron-right" : "chevron-down"}
                  size={20}
                  color={colors.text}
                />
                <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                  {roleName} (P:{roleUsers[0]?.role_priority ?? "?"})
                </ThemedText>
                <ThemedText style={[styles.sectionCount, { color: colors.textSecondary }]}>
                  ({roleUsers.length})
                </ThemedText>
              </Pressable>
              {!isCollapsed && (
                <ScrollView
                  horizontal={true}
                  showsHorizontalScrollIndicator={true}
                  contentContainerStyle={{ width: "auto", flexGrow: 1 }}
                >
                  <Table<AvailableUser>
                    data={roleUsers}
                    columns={columns}
                    keyExtractor={(item) => item.user_id}
                    sortable
                    defaultSort={{ key: "name", direction: "asc" }}
                    emptyMessage="No users"
                    emptyIcon="account-outline"
                    selectable
                    selectedKeys={selectedUserIds}
                    onSelectionChange={setSelectedUserIds}
                    rowActions={[
                      {
                        icon: "plus",
                        label: "Add to Position",
                        onPress: (row) => handleAddUser(row),
                      },
                    ]}
                  />
                </ScrollView>
              )}
            </View>
          );
        })}
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  headerTitleText: {
    gap: 2,
  },
  title: {
    fontSize: 16,
    fontWeight: "600",
  },
  subtitle: {
    fontSize: 12,
  },
  headerSearchContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    minWidth: 140,
  },
  headerSearchIcon: {
    marginRight: 6,
  },
  headerSearchInput: {
    flex: 1,
    fontSize: 14,
    minWidth: 100,
    ...Platform.select({
      web: { outlineStyle: "none" as any },
    }),
  },
  headerClearButton: {
    padding: 2,
    marginLeft: 4,
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
    gap: 8,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: "600",
    opacity: 0.6,
  },
  roleSection: {
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  sectionTitle: {
    fontSize: 15,
  },
  sectionCount: {
    fontSize: 13,
  },
  filterPopover: {
    width: 400,
    maxHeight: 320,
  },
  filterHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  sectionDivider: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    backgroundColor: "rgba(0,0,0,0.02)",
  },
  filterOption: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  filterEmpty: {
    paddingHorizontal: 12,
    paddingVertical: 16,
    alignItems: "center",
  },
  sourceChip: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  configurationRequired: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
    gap: 12,
  },
  configTitle: {
    marginTop: 16,
    textAlign: "center",
  },
  configMessage: {
    textAlign: "center",
    opacity: 0.7,
    maxWidth: 400,
    lineHeight: 22,
  },
  configInstructions: {
    marginTop: 24,
    padding: 20,
    borderRadius: 12,
    backgroundColor: "rgba(0,0,0,0.03)",
    gap: 12,
    maxWidth: 400,
  },
  configStepsTitle: {
    fontSize: 14,
    marginBottom: 4,
  },
  configStep: {
    flexDirection: "row",
    gap: 8,
    alignItems: "flex-start",
  },
  configStepNumber: {
    fontSize: 14,
    fontWeight: "600",
    opacity: 0.5,
    width: 20,
  },
  configStepText: {
    fontSize: 14,
    flex: 1,
    opacity: 0.7,
    lineHeight: 20,
  },
  configLink: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 8,
  },
  configLinkText: {
    color: "white",
    fontSize: 15,
    fontWeight: "600",
  },
});
